import { ComponentProps } from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { DIALOG_CLOSE_CLASS_NAME } from '@/components/ui/dialog';

import { cn } from '@/lib/utils';

const Sheet = (props: ComponentProps<typeof SheetPrimitive.Root>) => {
  return (
    <SheetPrimitive.Root
      data-slot="sheet"
      {...props}
    />
  );
};

const SheetTrigger = (props: ComponentProps<typeof SheetPrimitive.Trigger>) => {
  return (
    <SheetPrimitive.Trigger
      data-slot="sheet-trigger"
      {...props}
    />
  );
};

const SheetClose = (props: ComponentProps<typeof SheetPrimitive.Close>) => {
  return (
    <SheetPrimitive.Close
      data-slot="sheet-close"
      {...props}
    />
  );
};

const SheetPortal = (props: ComponentProps<typeof SheetPrimitive.Portal>) => {
  return (
    <SheetPrimitive.Portal
      data-slot="sheet-portal"
      {...props}
    />
  );
};

const SheetOverlay = (props: ComponentProps<typeof SheetPrimitive.Overlay>) => {
  const { className, ...restProps } = props;

  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 bg-overlay fixed inset-0 z-50',
        className,
      )}
      {...restProps}
    />
  );
};

const SheetContent = (
  props: ComponentProps<typeof SheetPrimitive.Content> & {
    side?: 'top' | 'right' | 'bottom' | 'left';
    showCloseButton?: boolean;
  },
) => {
  const {
    className,
    children,
    side = 'right',
    showCloseButton = true,
    ...restProps
  } = props;

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          'bg-popover data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition data-[state=closed]:duration-150 data-[state=closed]:ease-exit data-[state=open]:duration-200 data-[state=open]:ease-enter',
          side === 'right' &&
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 border-l sm:max-w-100 size-full',
          side === 'left' &&
            'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 border-r sm:max-w-100 size-full',
          side === 'top' &&
            'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b',
          side === 'bottom' &&
            'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t',
          className,
        )}
        {...restProps}
      >
        {children}
        {showCloseButton ? (
          <SheetPrimitive.Close
            className={cn(
              DIALOG_CLOSE_CLASS_NAME,
              'focus-visible:outline-ring absolute top-2.5 right-3 flex cursor-pointer items-center justify-center focus-visible:outline-2 disabled:pointer-events-none',
            )}
          >
            <XIcon className="size-4" />
          </SheetPrimitive.Close>
        ) : null}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
};

const SheetHeader = (props: ComponentProps<'div'>) => {
  const { className, ...restProps } = props;

  return (
    <div
      data-slot="sheet-header"
      className={cn(
        'border-border flex flex-col gap-0.5 border-b px-4 py-3 pr-12',
        className,
      )}
      {...restProps}
    />
  );
};

const SheetFooter = (props: ComponentProps<'div'>) => {
  const { className, ...restProps } = props;

  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        'border-border mt-auto flex flex-col gap-2 border-t px-4 py-3',
        className,
      )}
      {...restProps}
    />
  );
};

const SheetTitle = (props: ComponentProps<typeof SheetPrimitive.Title>) => {
  const { className, ...restProps } = props;

  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn(
        'text-foreground text-lg leading-7 font-semibold tracking-tight',
        className,
      )}
      {...restProps}
    />
  );
};

const SheetDescription = (
  props: ComponentProps<typeof SheetPrimitive.Description>,
) => {
  const { className, ...restProps } = props;

  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...restProps}
    />
  );
};

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
};
